import { Duration } from 'js-joda';
import { SetCookieOptions } from '../../utils/cookieUtils';
import { UserConsentLevel } from '../../utils/cookieConsentUtils';

const ONE_YEAR_MILLISECONDS = 1000 * 60 * 60 * 24 * 365;

type TockCookieDetails = SetCookieOptions & {
  consentCategory: UserConsentLevel;
};

export enum TockCookieName {
  GEO = 'tock_geo',
  SHIPPING_STATE = 'tock_shipping_state',
  LAT_LNG = 'tock_latlng',
  CHASE_SOURCE = 'tock_chase_source',
  HOMEPAGE_FS = 'HOMEPAGE_FS',
  WALKIN_WAITLIST = 'tock_walkin_waitlist',
  SYNDICATE = 'syndicate',
  CLS = 'tock_cls',
  GWUR_REFRESH = 'GWURRefresh',
  CURRENT_LOCATION_ACCEPTANCE = 'CURRENT_LOCATION_ACCEPTANCE',
  ACCESS = 'tock_access',
  ACTIVE_BUSINESS = 'tock_activeBusiness',
  TAX_MIGRATION_DIALOG = 'has_seen_tax_migration_dialog',
  LEGACY_TOCK_CONSENT = 'tock-cookie-consent',
  LEGACY_TOCK_ACCEPT_TERMS = 'tock-accept-terms',
  SHOP_ANNOUNCEMENT_BANNER = 'has_dismissed_shop_announcement_banner',
  TOCK_TEST = 'TOCKTEST',
  RWG_TOKENS = 'rwg_paid_redirect_tokens',
  CSRF = 'tock_csrf',
}

export const getTockCookieDetails = (cookieName: TockCookieName): TockCookieDetails => {
  const cookieDetails: { [key in TockCookieName]: TockCookieDetails } = {
    [TockCookieName.GEO]: {
      consentCategory: UserConsentLevel.REQUIRED,
      expirationFromNow: ONE_YEAR_MILLISECONDS,
      sameSiteNone: true,
      // Used to track user's metro area for search behavior
    },
    [TockCookieName.LAT_LNG]: {
      consentCategory: UserConsentLevel.REQUIRED,
      expirationFromNow: ONE_YEAR_MILLISECONDS,
      sameSiteNone: true,
      // Used to locate user's current location for search behavior - Possibly using redux state / metro data to populate this
    },
    [TockCookieName.SHIPPING_STATE]: {
      consentCategory: UserConsentLevel.REQUIRED,
      expirationFromNow: ONE_YEAR_MILLISECONDS,
      sameSiteNone: true,
      // Used to track user's shipping state for shop item eligibility
    },
    [TockCookieName.CHASE_SOURCE]: {
      consentCategory: UserConsentLevel.REQUIRED,
      expirationFromNow: Duration.ofDays(365 * 10).toMillis(),
      // Enables tracking origin from Chase Dining
    },
    [TockCookieName.HOMEPAGE_FS]: {
      consentCategory: UserConsentLevel.REQUIRED,
      expirationFromNow: Duration.ofDays(15).toMillis(),
      // Used to initialize FullStory
    },
    [TockCookieName.WALKIN_WAITLIST]: {
      consentCategory: UserConsentLevel.REQUIRED,
      // Allows a non-signed in user to pull information about their position on a waitlist via external link
    },
    [TockCookieName.SYNDICATE]: {
      consentCategory: UserConsentLevel.REQUIRED,
      expirationFromNow: Duration.ofDays(365).toMillis(),
      // Used to enable business-specific styles (e.g., colors)
    },
    [TockCookieName.CLS]: {
      consentCategory: UserConsentLevel.REQUIRED,
      // Used to enable Chase Dining session
    },
    [TockCookieName.GWUR_REFRESH]: {
      consentCategory: UserConsentLevel.REQUIRED,
      domainOverride: '.chase.com',
      // Used to refresh point values within Chase session
    },
    [TockCookieName.CURRENT_LOCATION_ACCEPTANCE]: {
      consentCategory: UserConsentLevel.REQUIRED,
      expirationFromNow: 5 * 60000,
      // Used to store user allowance of current location lookup
    },
    [TockCookieName.ACCESS]: {
      consentCategory: UserConsentLevel.REQUIRED,
      sameSiteNone: true,
      // Used for authentication'
    },
    [TockCookieName.ACTIVE_BUSINESS]: {
      consentCategory: UserConsentLevel.REQUIRED,
      expirationFromNow: Duration.ofDays(30).toMillis(),
      // Used to store context regarding user's current business in Dashboard
    },
    [TockCookieName.TAX_MIGRATION_DIALOG]: {
      consentCategory: UserConsentLevel.REQUIRED,
      // Used to remember whether user has seen an information dialog
    },
    [TockCookieName.LEGACY_TOCK_CONSENT]: {
      consentCategory: UserConsentLevel.REQUIRED,
      expirationFromNow: Duration.ofDays(365 * 10).toMillis(),
      // To be deprecated upon TrustArc integration
    },
    [TockCookieName.LEGACY_TOCK_ACCEPT_TERMS]: {
      consentCategory: UserConsentLevel.REQUIRED,
      expirationFromNow: Duration.ofDays(180).toMillis(),
      // To be deprecated upon TrustArc integration
    },
    [TockCookieName.SHOP_ANNOUNCEMENT_BANNER]: {
      consentCategory: UserConsentLevel.REQUIRED,
      expirationFromNow: ONE_YEAR_MILLISECONDS,
      // Used to remember whether user has dismissed shop announcement banner
    },
    [TockCookieName.TOCK_TEST]: {
      consentCategory: UserConsentLevel.REQUIRED,
      expirationFromNow: ONE_YEAR_MILLISECONDS,
      // Used to bypass certain auth / user challenge verification checks in Tock test environments
    },
    [TockCookieName.RWG_TOKENS]: {
      consentCategory: UserConsentLevel.REQUIRED,
      expirationFromNow: ONE_YEAR_MILLISECONDS,
      // Used for conversion tracking on RwG paid redirects
    },
    [TockCookieName.CSRF]: {
      consentCategory: UserConsentLevel.REQUIRED,
      sameSiteNone: true,
      // Used for csrf prevention
    },
  };

  return cookieDetails[cookieName];
};
